import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import { useFeedback } from '../../../../hooks/use-feedback';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import { IBudgetActionAddEditRequest } from '../../../../model/sge/budget-action/request/budget-action-add-edit-request';
import { IBudgetActionResponse } from '../../../../model/sge/budget-action/response/budget-action-response';
import { IBudgetProgramResponse } from '../../../../model/sge/budget-program/response/budget-program-response';
import { IBudgetUnitResponse } from '../../../../model/sge/budget-unit/response/budget-unit-response';
import { hasErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { formatCodeName } from '../../components/code-name-label';
import { BUDGET_ACTION_TYPES, BudgetActionForm, toBudgetActionForm, toBudgetActionRequest, validateBudgetAction } from './budget-action-form-utils';

interface BudgetActionDrawerProps {
    open: boolean;
    initial?: IBudgetActionResponse;
    initialBudgetUnitId?: number;
    budgetUnits: IBudgetUnitResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IBudgetActionAddEditRequest) => void;
}

export function BudgetActionDrawer({ open, initial, initialBudgetUnitId, budgetUnits, isSaving, onClose, onSave }: BudgetActionDrawerProps) {
    const { showError } = useFeedback();
    const [form, setForm] = useState<BudgetActionForm>({});
    const [budgetPrograms, setBudgetPrograms] = useState<IBudgetProgramResponse[]>([]);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateBudgetAction(form);
    const errors = showValidationErrors ? validation : {};
    const budgetUnitId = form.budgetUnitId;

    useEffect(() => {
        if (!open) return;
        setForm(toBudgetActionForm(initial, initialBudgetUnitId));
        setShowValidationErrors(false);
    }, [open, initial, initialBudgetUnitId]);

    // Programas da unidade selecionada (cascata unidade → programa)
    useEffect(() => {
        if (!budgetUnitId) {
            setBudgetPrograms([]);
            return;
        }
        BudgetProgramAppService.list({ budgetUnitId })
            .then(setBudgetPrograms)
            .catch((error: any) => {
                setBudgetPrograms([]);
                showError('Não foi possível carregar os programas orçamentários', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budgetUnitId]);

    function update(patch: Partial<BudgetActionForm>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave(toBudgetActionRequest(form));
    }

    return (
        <FormDrawer
            open={open}
            icon={<TrackChangesIcon />}
            title={isEdit ? 'Editar ação orçamentária' : 'Nova ação orçamentária'}
            subtitle={initial ? formatCodeName(initial) : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar ação'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Código</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.code || ''} onChange={(e) => update({ code: e.target.value })} error={!!errors.code} />
                        <FieldError message={errors.code} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Nome</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.name || ''} onChange={(e) => update({ name: e.target.value })} error={!!errors.name} />
                        <FieldError message={errors.name} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Classificação">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Unidade orçamentária</InputLabel>
                        <Select
                            fullWidth
                            disabled={isEdit}
                            value={form.budgetUnitId ?? ''}
                            onChange={(e) => update({ budgetUnitId: parseNumberInput(e.target.value), budgetProgramId: undefined })}
                            error={!!errors.budgetUnitId}
                        >
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{formatCodeName(u)}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.budgetUnitId} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <InputLabel required>Programa orçamentário</InputLabel>
                        <Select
                            fullWidth
                            disabled={isEdit || !budgetUnitId}
                            value={form.budgetProgramId ?? ''}
                            onChange={(e) => update({ budgetProgramId: parseNumberInput(e.target.value) })}
                            error={!!errors.budgetProgramId}
                        >
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {budgetPrograms.map((p) => <MenuItem key={p.id} value={p.id}>{formatCodeName(p)}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.budgetProgramId} />
                        {!budgetUnitId && <FormHelperText>Selecione a unidade orçamentária para listar os programas</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <InputLabel required>Tipo</InputLabel>
                        <Select fullWidth value={form.actionType ?? ''} onChange={(e) => update({ actionType: parseNumberInput(e.target.value) })} error={!!errors.actionType}>
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {BUDGET_ACTION_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.id} - {t.label}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.actionType} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
